/**
 * Workshop pack A: seven courses after the classics.
 *
 * Brass Meadows → Flux Reservoir. Each factory paints a 160×30 grid with the
 * shared helpers in `paint.ts` and returns plain `LevelData`.
 */

import { Terrain, type LevelData, type ObjectPlacement } from '../game/types'
import {
  blank,
  box,
  catwalk,
  coinArc,
  coins,
  carve,
  courseShell,
  finish,
  floor,
  ledge,
  place,
  placeRow,
  pipe,
  scenery,
  stair,
} from './paint'

/** Gentle follow-up to Green Hills: rolling floor, short gaps, one pipe pair. */
function brassMeadows(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 38, 25)
  floor(g, 42, 70, 25)
  floor(g, 74, 108, 25)
  floor(g, 112, 159, 25)

  scenery(o, [
    [8, 8, 'cloud'],
    [40, 7, 'cloud2'],
    [72, 9, 'cloud'],
    [110, 8, 'cloud2'],
    [18, 23, 'hill'],
    [55, 24, 'bush'],
    [96, 23, 'hill'],
    [130, 24, 'bush'],
  ])
  placeRow(o, 'fence', 140, 24, 143)

  place(o, 'rivet', 12, 24)
  place(o, 'crate', 18, 21)
  place(o, 'brick', 20, 21)
  place(o, 'crate-expansion-cell', 22, 21)
  place(o, 'rivet', 28, 24)

  pipe(o, 32, 23, 3)
  coins(o, 32, 33, 21)
  pipe(o, 48, 23, 3)
  coins(o, 48, 49, 21)
  box(g, 45, 24, 45, 24, Terrain.HARD)

  coinArc(o, 39, 41, 24)
  place(o, 'rivet', 58, 24)
  coinArc(o, 71, 73, 24)
  place(o, 'scout', 82, 24)
  place(o, 'rivet', 90, 24)
  coins(o, 84, 88, 24)
  place(o, 'crate', 100, 21)
  place(o, 'crate', 102, 21, { contains: 'spare-battery' })
  coinArc(o, 109, 111, 24)

  place(o, 'rivet', 120, 24)
  place(o, 'rivet', 128, 24)
  place(o, 'expansion-cell', 134, 24)
  coins(o, 136, 140, 24)
  finish(o, 150, 25)

  return courseShell('Brass Meadows', 'overworld', 380, 3, 24, g, o)
}

/** Pipe ladder and hard-block stairs with a spare battery on the ridge. */
function copperRidge(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 44, 25)
  floor(g, 49, 159, 25)

  scenery(o, [
    [10, 8, 'cloud'],
    [50, 7, 'cloud2'],
    [90, 9, 'cloud'],
    [130, 8, 'cloud2'],
    [20, 23, 'hill'],
    [70, 24, 'bush'],
  ])

  place(o, 'rivet', 10, 24)
  place(o, 'crate', 16, 21)
  place(o, 'crate-expansion-cell', 18, 21)

  pipe(o, 24, 23, 3)
  pipe(o, 30, 22, 4)
  pipe(o, 36, 21, 5)
  coins(o, 24, 25, 21)
  coins(o, 30, 31, 20)
  coins(o, 36, 37, 19)
  place(o, 'spare-battery', 38, 20)

  coinArc(o, 45, 48, 24)
  place(o, 'scout', 56, 24)
  place(o, 'rivet', 62, 24)
  place(o, 'brick', 58, 21)
  place(o, 'crate', 59, 21)
  place(o, 'brick', 60, 21)

  stair(g, 80, 24, 8, 1, 24)
  coins(o, 86, 87, 17)
  place(o, 'spare-battery', 87, 16)
  place(o, 'checkpoint', 92, 24)

  place(o, 'rivet', 100, 24)
  place(o, 'rivet', 108, 24)
  place(o, 'scout', 116, 24)
  place(o, 'crate-ember-cell', 124, 21)
  coins(o, 120, 126, 24)
  place(o, 'rivet', 136, 24)
  finish(o, 150, 25)

  return courseShell('Copper Ridge', 'overworld', 360, 3, 24, g, o)
}

/** Ember cells, springs, and short lava pools bridged by catwalks. */
function emberTrail(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 30, 25)
  floor(g, 40, 70, 25)
  floor(g, 80, 110, 25)
  floor(g, 120, 159, 25)

  scenery(o, [
    [12, 8, 'cloud'],
    [60, 7, 'cloud2'],
    [100, 9, 'cloud'],
    [24, 23, 'hill'],
    [90, 24, 'bush'],
  ])

  place(o, 'rivet', 10, 24)
  place(o, 'crate-ember-cell', 16, 21)
  place(o, 'note-block', 22, 24)
  coins(o, 22, 24, 22)
  place(o, 'ember-cell', 28, 24)

  placeRow(o, 'lava', 31, 27, 39)
  catwalk(g, 32, 24, 34)
  catwalk(g, 36, 23, 38)
  coinArc(o, 31, 39, 25)
  place(o, 'checkpoint', 44, 24)

  place(o, 'spring', 52, 24)
  placeRow(o, 'platform', 54, 20, 60)
  coins(o, 55, 59, 19)
  place(o, 'rivet', 64, 24)
  place(o, 'scout', 68, 24)

  placeRow(o, 'lava', 71, 27, 79)
  catwalk(g, 72, 24, 74)
  catwalk(g, 76, 23, 78)
  place(o, 'prism', 76, 22)

  place(o, 'crate-ember-cell', 88, 21)
  place(o, 'note-block', 94, 24)
  place(o, 'rivet', 100, 24)
  place(o, 'spring', 106, 24)
  placeRow(o, 'platform', 108, 18, 114)
  coins(o, 109, 113, 17)

  placeRow(o, 'lava', 111, 27, 119)
  // Landing after the high platform chain.
  place(o, 'checkpoint', 124, 24)
  place(o, 'rivet', 132, 24)
  place(o, 'scout', 140, 24)
  coins(o, 134, 138, 24)
  finish(o, 150, 25)

  return courseShell('Ember Trail', 'overworld', 350, 3, 24, g, o)
}

/** First night course: wisps, coin lines, three walkable gaps. */
function nightShift(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 36, 25)
  floor(g, 40, 72, 25)
  floor(g, 76, 112, 25)
  floor(g, 116, 159, 25)

  scenery(o, [
    [14, 6, 'cloud'],
    [80, 5, 'cloud2'],
    [30, 23, 'hill'],
    [100, 24, 'bush'],
  ])

  place(o, 'rivet', 11, 24)
  place(o, 'crate', 17, 21)
  place(o, 'crate', 19, 21)
  place(o, 'crate-expansion-cell', 21, 21)
  place(o, 'wisp', 26, 20)
  coins(o, 24, 30, 24)

  coinArc(o, 37, 39, 24)
  place(o, 'rivet', 48, 24)
  place(o, 'wisp', 56, 18)
  place(o, 'scout', 62, 24)
  coins(o, 50, 58, 24)
  place(o, 'checkpoint', 68, 24)

  coinArc(o, 73, 75, 24)
  place(o, 'wisp', 84, 16)
  place(o, 'rivet', 88, 24)
  place(o, 'rivet', 96, 24)
  place(o, 'crate', 102, 21, { contains: 'spare-battery' })
  coins(o, 90, 100, 24)

  coinArc(o, 113, 115, 24)
  place(o, 'wisp', 124, 19)
  place(o, 'rivet', 130, 24)
  place(o, 'prism', 138, 24)
  coins(o, 132, 140, 24)
  finish(o, 150, 25)

  return courseShell('Night Shift', 'night', 360, 3, 24, g, o)
}

/** Night vents, a shuttle over a pit, and a shield prism. */
function lanternLane(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 42, 25)
  floor(g, 56, 159, 25)

  scenery(o, [
    [10, 6, 'cloud'],
    [70, 5, 'cloud2'],
    [120, 7, 'cloud'],
  ])

  place(o, 'rivet', 10, 24)
  place(o, 'crate-expansion-cell', 16, 21)
  pipe(o, 22, 23, 3)
  pipe(o, 28, 22, 4)
  pipe(o, 34, 21, 5)
  coins(o, 22, 23, 21)
  coins(o, 28, 29, 20)
  coins(o, 34, 35, 19)
  place(o, 'wisp', 40, 18)

  place(o, 'moving-platform-h', 44, 23, { range: 4, speed: 0.45 })
  placeRow(o, 'platform', 50, 23, 52)
  placeRow(o, 'platform', 53, 23, 55)
  coinArc(o, 43, 55, 25)
  place(o, 'checkpoint', 60, 24)

  place(o, 'wisp', 68, 17)
  place(o, 'rivet', 72, 24)
  place(o, 'scout', 80, 24)
  place(o, 'prism', 86, 24)
  place(o, 'crate', 92, 21)
  place(o, 'brick', 93, 21)
  place(o, 'crate', 94, 21, { contains: 'ember' })

  place(o, 'wisp', 104, 15)
  place(o, 'rivet', 110, 24)
  place(o, 'rivet', 118, 24)
  stair(g, 124, 24, 6, 1, 24)
  coins(o, 128, 129, 19)
  place(o, 'spare-battery', 129, 18)
  place(o, 'rivet', 138, 24)
  coins(o, 140, 145, 24)
  finish(o, 150, 25)

  return courseShell('Lantern Lane', 'night', 340, 3, 24, g, o)
}

/** Underground machinery: saws, shelves, lava sump, chimney catwalks. */
function gearworks(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  box(g, 0, 0, 159, 2, Terrain.GROUND)
  floor(g, 0, 24, 25)
  floor(g, 34, 55, 25)
  floor(g, 66, 159, 25)

  carve(g, 25, 25, 33, 27)
  carve(g, 56, 25, 65, 27)
  carve(g, 70, 4, 78, 24)

  box(g, 25, 28, 33, 29, Terrain.GROUND)
  placeRow(o, 'lava', 25, 27, 33)
  catwalk(g, 26, 24, 28)
  catwalk(g, 30, 23, 32)
  coins(o, 26, 28, 23)

  place(o, 'rivet', 10, 24)
  place(o, 'crate', 14, 21)
  place(o, 'crate-expansion-cell', 16, 21)
  ledge(g, 18, 22, 22, 24, Terrain.HARD)
  place(o, 'saw', 20, 19, { range: 3, speed: 0.35 })
  coins(o, 18, 21, 21)

  place(o, 'scout', 40, 24)
  place(o, 'brick', 42, 24)
  place(o, 'brick', 43, 24)
  place(o, 'rivet', 48, 24)
  place(o, 'checkpoint', 52, 24)

  box(g, 56, 28, 65, 29, Terrain.GROUND)
  placeRow(o, 'lava', 56, 27, 65)
  catwalk(g, 57, 24, 59)
  catwalk(g, 61, 23, 63)
  place(o, 'prism', 61, 22)

  const chimney: Array<[number, number, number]> = [
    [70, 22, 75],
    [72, 20, 77],
    [70, 18, 75],
    [72, 16, 77],
    [70, 14, 75],
    [72, 12, 77],
    [70, 10, 75],
    [72, 8, 77],
  ]
  for (const [x0, ty, x1] of chimney) {
    catwalk(g, x0, ty, x1)
    coins(o, x0 + 1, x1 - 1, ty - 1)
  }
  place(o, 'wisp', 76, 16)
  place(o, 'spare-battery', 76, 11)

  ledge(g, 80, 8, 90, 8, Terrain.HARD)
  place(o, 'rivet', 84, 7)
  place(o, 'moving-platform', 92, 24, { range: 12, speed: 0.35 })
  placeRow(o, 'platform', 94, 12, 98)
  ledge(g, 100, 12, 159, 24, Terrain.GROUND)
  place(o, 'rivet', 110, 11)
  place(o, 'scout', 120, 11)
  place(o, 'crate-prism', 130, 11)
  coins(o, 112, 118, 11)
  finish(o, 150, 12)

  return courseShell('Gearworks', 'underground', 380, 3, 24, g, o)
}

/** Lava hops, shardlings, and a spring launch home. */
function fluxReservoir(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  box(g, 0, 0, 159, 2, Terrain.GROUND)
  floor(g, 0, 20, 25)
  floor(g, 130, 159, 25)

  place(o, 'rivet', 8, 24)
  place(o, 'crate-expansion-cell', 14, 21)
  place(o, 'checkpoint', 18, 24)

  placeRow(o, 'lava', 21, 27, 129)
  // Stepping stones across the reservoir.
  const stones: Array<[number, number, number]> = [
    [24, 24, 26],
    [30, 23, 32],
    [36, 24, 38],
    [42, 22, 44],
    [48, 24, 50],
    [54, 23, 56],
    [60, 24, 62],
    [66, 22, 68],
    [72, 24, 74],
    [78, 23, 80],
    [84, 24, 86],
    [90, 22, 92],
    [96, 24, 98],
    [102, 23, 104],
    [108, 24, 110],
    [114, 22, 116],
    [120, 24, 122],
    [126, 23, 128],
  ]
  for (const [x0, ty, x1] of stones) {
    catwalk(g, x0, ty, x1)
    coins(o, x0, x1, ty - 1)
  }

  place(o, 'shardling', 44, 21)
  place(o, 'prism', 68, 21)
  place(o, 'shardling', 92, 21)
  place(o, 'checkpoint', 110, 23)
  place(o, 'spare-battery', 116, 21)

  place(o, 'spring', 134, 24)
  place(o, 'note-block', 138, 24)
  place(o, 'rivet', 144, 24)
  coins(o, 140, 146, 24)
  finish(o, 150, 25)

  return courseShell('Flux Reservoir', 'underground', 350, 3, 24, g, o)
}

const factories = [
  brassMeadows,
  copperRidge,
  emberTrail,
  nightShift,
  lanternLane,
  gearworks,
  fluxReservoir,
] as const

export const WORKSHOP_A_LIST: LevelData[] = factories.map((f) => f())

export const WORKSHOP_A_COURSES: Record<string, LevelData> = Object.fromEntries(
  WORKSHOP_A_LIST.map((c) => [c.name, c]),
)
